import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { createFederatedIsland, loadRemoteModule } from '@makemydeal/dr-shared-ui-utils';
import type { ComponentType, FunctionComponent } from 'react';
import { lazy } from 'react';
import { useSelector } from 'react-redux';

/*
  Render a VinSolutions remote by whichever mechanism is currently correct.

    toggle off  lazy(loadRemoteModule(...))  the remote renders INSIDE this React 18 tree
    toggle on   createFederatedIsland(...)   the remote renders in its OWN React root

  This is the only place the choice is made — call sites just use the returned component.

  WHY there are two mechanisms, what flipping the toggle changes for callers, and why this lives
  in dr-shared-components rather than beside createFederatedIsland: see MODULE_FEDERATION.md at
  the repo root.
*/

/**
 * The VinSolutions remotes dr-dash consumes, and the modules they expose.
 *
 * Enumerated rather than left as `string` because both halves are resolved at RUNTIME, from a
 * manifest fetched over the network: a typo in either compiles cleanly, ships, and then surfaces as
 * a failed panel in front of a dealer. Adding a remote or an expose means adding it here first —
 * and it has to exist in the module-federation manifest the app loads, which this cannot check.
 */
export type VinRemoteName = 'customer';
export type VinRemoteExpose =
    | './CustomerSidebarModule'
    | './CustomerEditModule'
    | './FindOrCreateModule'
    | './SimplifiedCardModule';

/**
 * Build a component for a VinSolutions remote module.
 *
 * Both mechanisms suspend while the remote loads, so a call site needs a `<Suspense>` boundary
 * above it either way — and keeps the same one when the toggle flips.
 *
 * Reads the toggle through `useSelector`, so the returned component must render under a react-redux
 * `Provider`. Every call site already does, being inside an app tree; specs that merely import a
 * barrel reaching one of them do not, which is why those stub this module rather than provide a
 * store (see MODULE_FEDERATION.md).
 */
export function createRemoteModule<P extends object>(remoteName: VinRemoteName, exposeName: VinRemoteExpose): FunctionComponent<P> {
    const Island = createFederatedIsland<P>(remoteName, exposeName);
    // Narrowed to ComponentType<P> rather than LazyExoticComponent<ComponentType<P>>: spreading a
    // generic P into a LazyExoticComponent does not typecheck (TS cannot prove P satisfies
    // PropsWithRef<P>), and lazy() is a valid ComponentType at every use site here. The cast hides
    // that this one suspends; the island suspends too, so the requirement on callers is the same.
    const Legacy = lazy(() => loadRemoteModule(remoteName, exposeName)) as unknown as ComponentType<P>;

    const RemoteModule = (props: P) => {
        const federatedIslandsEnabled = useSelector(featureToggleSelectors.isFederatedIslandsEnabledAfterBootstrap);

        return federatedIslandsEnabled ? <Island {...props} /> : <Legacy {...props} />;
    };

    RemoteModule.displayName = `RemoteModule(${remoteName}${exposeName})`;

    return RemoteModule;
}
